/*
 * <liva-bundle-builder> — PDP bundle builder (SPEC.md §5, §7, §8, §24).
 *
 * Phase 7a (SPEC §24): builder-only PDP. A slim progress ladder (points
 * 1, 2, 3 and +1 free, rendered by Liquid, fed by the total quantity) and
 * stepper cards ("+" pill, then "− n +" once a flavour is in the bundle).
 * No mode toggle, no boxes, no upper limit on the total quantity; the
 * per-flavour cap is min(stock cap, theme setting bundle_max_per_product).
 *
 * Phase 7b (SPEC §25): the builder mirrors the cart. State comes from
 * GET /cart.js (packs = cart lines whose variant is in a group list; every
 * other line is kept for pricing only) and is re-synced on the theme's
 * cart:change. Steppers hold the DESIRED quantity per flavour (what is in
 * the cart plus local additions). "+" never writes to the cart until Add
 * to cart, which posts the additions only; "−" below the cart baseline
 * writes /cart/change.js after a debounce, serialised, one in flight.
 * No sessionStorage.
 *
 * Kept from earlier phases: the Storefront API cartCreate preview with the
 * optimistic total, session cache and low-priority prefetch (SPEC §19.8;
 * now over the full desired cart); 422 handling (a cart:error after submit
 * marks the affected flavour's card unavailable for the session, SPEC
 * §19.4 / §24); the fail-safe that restores the Liquid single-pack form on
 * any init exception, explicit listener removal and blob normalisation
 * (SPEC §20); the one availability rule _canAdd and the money-format
 * resolver (SPEC §21).
 *
 * Liquid renders the element in its final DOM position and pre-renders the
 * 0-pack body (ladder, hint, cards; SPEC §23, §24), so init only binds
 * events and fills state — nothing is moved or unhidden here. The ladder
 * markup is never rewritten: JS sets data-count and is-reached, and writes
 * the engine's per-pack figures into the text of points 2–4 (SPEC §28.2).
 *
 * Reads the JSON blob rendered by snippets/bundle-data.liquid, submits
 * additions through the theme's ProductForm, removes through
 * /cart/change.js exactly like the theme's own line-item-quantity.
 * Every user-visible string comes from the blob (locale keys only).
 * Browser floor (SPEC §20.5): Safari 12.1 / Chrome 80 / Firefox 75 /
 * Samsung Internet 12 — ES2019 syntax only (no optional chaining, no
 * Array.prototype.at, no addEventListener signal option).
 */
(function () {
  'use strict';

  if (window.customElements.get('liva-bundle-builder')) {
    return;
  }

  var FREE_EVERY = 4;                 // every 4th pack is free (BXGY 3+1); the ladder has 4 points
  var DEFAULT_MAX_PER_PRODUCT = 10;   // theme setting bundle_max_per_product fallback
  var MAX_PER_PRODUCT_CEILING = 50;   // the setting's upper bound (settings_schema.json), enforced on the blob too
  var GROUP_SOLD_OUT_CLASS = 'bundle--group-sold-out'; // Liquid fallback, SPEC §19.9
  var PREVIEW_DEBOUNCE_MS = 50;      // Phase 4c: the optimistic total covers the gap
  var SPINNER_DELAY_MS = 400;        // spinner only when the engine is this late
  var PREFETCH_CONCURRENCY = 2;
  var REMOVE_DEBOUNCE_MS = 1500;      // "−" below the cart baseline writes after this quiet period (SPEC §25)
  var BUSY_MIN_MS = 400;              // SPEC §42: Impact's custom-button animation needs this long to be seen
  var BUSY_CEILING_MS = 5000;         // SPEC §42.1: a lost request can never leave the button busy
  var DRAWER_SWAP_DELAY_MS = 1250;    // theme.js CartDrawer swaps its inner HTML this late after a non-variant:add cart:change
  var WRITE_ROUNDS = 3;               // a removal may need a second write when the variant is split into paid + free lines
  var PREVIEW_QUERY =
    'mutation BundlePreview($lines: [CartLineInput!]!, $country: CountryCode!) @inContext(country: $country) {' +
    ' cartCreate(input: { lines: $lines, buyerIdentity: { countryCode: $country } }) {' +
    ' cart { cost { totalAmount { amount currencyCode } }' +
    ' lines(first: 50) { nodes { quantity merchandise { ... on ProductVariant { id } }' +
    ' cost { amountPerQuantity { amount } } discountAllocations { discountedAmount { amount } } } } }' +
    ' userErrors { field message } } }';

  function routeRoot() {
    var routes = window.Shopify && window.Shopify.routes;
    return (routes && routes.root) || '/';
  }

  function toCents(amount) {
    var n = parseFloat(amount);
    return isNaN(n) ? 0 : Math.round(n * 100);
  }

  function formatMoney(cents, format) {
    var amount = (cents / 100).toFixed(2);
    var withComma = amount.replace('.', ',');
    var noDecimals = String(Math.round(cents / 100));
    return String(format || '{{amount}}')
      .replace(/\{\{\s*amount\s*\}\}/, amount)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, noDecimals)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, withComma)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, noDecimals)
      .replace(/\{\{\s*amount_with_apostrophe_separator\s*\}\}/, amount);
  }

  function interpolate(str, params) {
    return String(str || '').replace(/\{(\w+)\}/g, function (m, key) {
      return Object.prototype.hasOwnProperty.call(params, key) ? params[key] : m;
    });
  }

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // Blob values are server-rendered but normalised anyway (SPEC §20.4,
  // §35.2): ids and prices must be finite numbers, the swatch a hex colour,
  // swatchImage an https or protocol-relative URL, flavourNote a string
  // (anything else renders no note line). Returns null for an unusable entry.
  function normalizeProduct(raw) {
    if (!raw || typeof raw !== 'object') return null;
    var variantId = parseInt(raw.variantId, 10);
    var productId = parseInt(raw.productId, 10);
    var price = Number(raw.price);
    if (!isFinite(variantId) || !isFinite(productId) || !isFinite(price)) return null;
    var inventoryQuantity = Number(raw.inventoryQuantity);
    var swatch = typeof raw.swatch === 'string' && /^#[0-9a-f]{3,8}$/i.test(raw.swatch) ? raw.swatch : '#eee';
    var swatchImage = typeof raw.swatchImage === 'string' && /^(https:)?\/\//i.test(raw.swatchImage) ? raw.swatchImage : null;
    return {
      productId: productId,
      variantId: variantId,
      fullTitle: String(raw.fullTitle || ''),
      flavour: String(raw.flavour || ''),
      flavourNote: typeof raw.flavourNote === 'string' ? raw.flavourNote : '',
      swatch: swatch,
      swatchImage: swatchImage,
      price: price,
      available: raw.available === true,
      inventoryPolicy: raw.inventoryPolicy === 'deny' ? 'deny' : 'continue',
      inventoryQuantity: isFinite(inventoryQuantity) ? inventoryQuantity : null
    };
  }

  // Wraps an event handler so an exception is logged and never propagates
  // (SPEC §20.2). The builder state may be stale after one, but the form
  // and the page keep working.
  function guard(element, fn) {
    return function (event) {
      if (element._dead) return; // fail-safe ran: the builder is inert
      try {
        fn(event);
      } catch (error) {
        console.error('[liva-bundle-builder] handler failed', error);
      }
    };
  }

  class LivaBundleBuilder extends HTMLElement {
    connectedCallback() {
      // Sold-out group (SPEC §19.9): Liquid hid the wrapper and the page
      // is the plain 1-pack PDP; the builder never initialises.
      if (this.classList.contains(GROUP_SOLD_OUT_CLASS)) return;

      // Fail-safe (SPEC §20.2): the Liquid page is a complete single-pack
      // purchase; if anything in init throws, put that baseline back and
      // hide the builder rather than leave the form half-switched.
      try {
        this._init();
      } catch (error) {
        console.error('[liva-bundle-builder] init failed; single-pack form kept', error);
        this._failSafe();
      }
    }

    _init() {
      var section = this.closest('.shopify-section');
      var blobEl = (section && section.querySelector('[data-bundle-data]')) ||
        document.querySelector('[data-bundle-data]');
      if (!blobEl) return;

      try {
        this._data = JSON.parse(blobEl.textContent);
      } catch (error) {
        console.warn('[liva-bundle-builder] invalid bundle data', error);
        return;
      }

      this._strings = this._data.strings || {};
      // Card swatch style (SPEC §22): theme setting bundle_swatch_style;
      // anything but "image" is colour.
      this._swatchStyle = this._data.swatchStyle === 'image' ? 'image' : 'color';
      // Per-flavour cap from the theme setting (SPEC §24); a lower stock cap
      // wins. Clamped to the setting's own 1–50 range (SPEC §35.2); anything
      // that is not an integer falls back to the default.
      var maxPerProduct = parseInt(this._data.maxPerProduct, 10);
      this._maxPerProduct = isFinite(maxPerProduct) ? Math.min(MAX_PER_PRODUCT_CEILING, Math.max(1, maxPerProduct)) : DEFAULT_MAX_PER_PRODUCT;
      // The cart URL comes from routes.cart_url; only a same-origin path is
      // accepted (SPEC §35.2), else the routes root + "cart".
      var cartUrl = this._data.cartUrl;
      this._data.cartUrl = typeof cartUrl === 'string' && cartUrl.charAt(0) === '/' && cartUrl.charAt(1) !== '/' ? cartUrl : routeRoot() + 'cart';
      this._formId = this.getAttribute('data-form-id');
      this._items = [];               // [{ variantId, quantity }] in first-added order
      this._byVariant = Object.create(null); // no prototype keys ("constructor") to hit
      this._currentGroupKey = null;
      this._currentProduct = null;
      this._data.currentProductId = parseInt(this._data.currentProductId, 10);

      var self = this;
      (this._data.groups || []).forEach(function (group) {
        group.products = (group.products || []).map(normalizeProduct).filter(Boolean);
        group.products.forEach(function (product) {
          self._byVariant[product.variantId] = product;
          if (product.productId === self._data.currentProductId) {
            self._currentGroupKey = group.key;
            self._currentProduct = product;
          }
        });
      });

      // A product outside every group (discontinued) keeps the 1-pack form.
      if (!this._currentProduct) {
        this._failSafe();
        return;
      }

      this._cart = { packs: Object.create(null), others: [] }; // last /cart.js snapshot (SPEC §25)
      this._cartSeq = 0;             // bumps on every apply; a slower fetch never overwrites a newer state
      this._pendingRemovals = Object.create(null); // variantId -> true while a removal is armed, queued or in flight
      this._removeTimers = Object.create(null);    // variantId -> debounce timer
      this._writeQueued = Object.create(null);     // variantId -> true while waiting in the write chain
      this._writeChain = Promise.resolve();        // cart writes run one after another
      this._ownDispatch = false;     // set around our own cart:change dispatch
      this._lastOwnDispatch = 0;
      this._resubmitting = false;    // our re-dispatched submit passes through the capture handler
      this._busy = false;            // submit waiting for pending removals: button shows its busy state
      this._lastIncremented = null;   // variant id, 422 fallback (SPEC §19.4)
      this._buttonCache = null;
      this._holdBusy = false;        // SPEC §42: keep aria-busy until the add answered and BUSY_MIN_MS passed
      this._submitAt = 0;
      this._releaseTimer = null;
      this._ceilingTimer = null;
      this._busyObserver = null;
      this._barButtons = null;       // SPEC §44: the theme bar's (product-quick-add) buttons, found once
      this._preview = null;          // { total, freeByPrice, optimistic? } | null
      this._previewPending = false;  // user state still waiting for the engine
      this._previewTimer = null;
      this._previewAbort = null;
      this._previewSeq = 0;
      this._spinnerTimer = null;
      this._loading = false;
      this._cache = {};              // variant key -> engine result
      this._cacheByPrice = {};       // price-signature key -> engine result (SPEC §19.8)
      this._prefetchQueue = [];
      this._prefetchInFlight = [];
      this._prefetchKeys = {};
      this._unavailable = {};        // variantId -> true after a 422 (SPEC §5.6, §19)
      this._tiers = {};              // ladder per-pack figures (SPEC §28.2): n -> { units, total, perPack, optimistic }
      this._tierSeq = 0;             // a slower tier response never overwrites a newer state
      this._tierTimer = null;
      this._tierAbort = null;        // one AbortController per tier batch (SPEC §35.2): the next batch aborts it

      this._bind();
      this._syncForm();
      // Speculative init pass (SPEC §36.1): the tier batch and the prefetch
      // start now for the assumed state (no picks, cart not yet known) so the
      // ladder does not wait for the cart round trip. When /cart.js is
      // applied, _applyCart compares the tier line-set signature of the real
      // state with this one: identical → the speculative answers are the
      // answers (only the ATC preview is scheduled); different → the
      // speculative batch is aborted and one pass runs for the real state.
      // If the cart fetch fails (fail-soft, SPEC §34.2 F19) the speculative
      // pass stands.
      this._schedulePreview();
      this._speculativeSignature = this._tierSignature();
      this._cartApplied = false;
      this._renderAll();             // empty state first: identical to the Liquid pre-render
      this._fetchCart('external');   // then the cart (SPEC §25.1)
    }

    disconnectedCallback() {
      this._unbind();
      this._releaseBusy();
      this._clearRemovalTimers();
      this._cancelPreview();
      this._abortPrefetches(true);
      if (this._priceCache && !this._dead) this._restorePrice();
    }

    // Restore the no-JS baseline after a failed init (SPEC §20.2): the
    // single-pack inputs enabled, builder inputs and mode attribute gone,
    // the theme's own button markup back, the builder hidden.
    _failSafe() {
      this._dead = true;   // deferred callbacks become no-ops
      try { this._unbind(); this._clearRemovalTimers(); } catch (e) { /* noop */ }
      try { this._cancelPreview(); this._abortPrefetches(true); } catch (e) { /* noop */ }
      var form = this._form();
      if (form) {
        var idInput = form.elements.namedItem('id');
        if (idInput) idInput.disabled = false;
        var quantityInput = document.getElementById(this._formId + '-quantity');
        if (quantityInput) { quantityInput.disabled = false; quantityInput.value = 1; }
      }
      var holder = this.querySelector('[data-bundle-inputs]');
      if (holder && holder.parentNode) holder.parentNode.removeChild(holder);
      var body = this.querySelector('[data-bundle-body]');
      if (body) body.hidden = true;
      var root = this.closest('.product-info');
      if (root) root.removeAttribute('data-bundle-mode');
      this._restorePrice();
      this._releaseBusy();
      (this._buttonCache || []).forEach(function (entry) {
        // SPEC §42: the markup goes back into the theme's content element, so the
        // custom-button's loader span and the node it animates survive.
        var target = entry.target && entry.el.contains(entry.target) ? entry.target : entry.el;
        target.innerHTML = entry.html;
        entry.el.disabled = entry.disabled;
        if (entry.type) entry.el.type = entry.type;                   // SPEC §44: a bar button back to submit
        entry.el.removeAttribute('data-bundle-scroll-to-picker');
      });
      this.hidden = true;
    }

    /* -------------------------------------------------------------- state
       The cart is the source of truth (SPEC §25). _cart is the last
       /cart.js snapshot: packs = lines whose variant is in a group list
       (quantity summed over the paid and the BXGY-free line, the paid
       line's key kept for removals), others = every other line, kept for
       the price preview only. _items holds the DESIRED quantity per pack
       variant: the cart baseline plus local additions, or below the
       baseline while a removal is pending. */

    _parseCart(cart) {
      var self = this;
      var packs = Object.create(null);
      var others = [];
      var items = cart && Array.isArray(cart.items) ? cart.items : [];
      items.forEach(function (line) {
        var vid = parseInt(line.variant_id, 10);
        var qty = parseInt(line.quantity, 10);
        if (!isFinite(vid) || !isFinite(qty) || qty <= 0) return;
        if (self._byVariant[vid]) {
          var entry = packs[vid] || (packs[vid] = { quantity: 0, freeQuantity: 0, paidKey: null, keys: [] });
          entry.quantity += qty;
          entry.keys.push(String(line.key));
          if (Number(line.final_line_price) > 0) {
            if (!entry.paidKey) entry.paidKey = String(line.key);
          } else {
            entry.freeQuantity += qty;
          }
        } else {
          others.push({ variantId: vid, quantity: qty, originalLinePrice: Number(line.original_line_price) || 0 });
        }
      });
      return { packs: packs, others: others };
    }

    // Reconciles desired quantities with a cart snapshot. Modes:
    //   'external' — init GET, the theme's / drawer's cart:change, bfcache:
    //                local additions (desired − old cart) survive on top of
    //                the new cart quantity; a variant with a pending removal
    //                keeps the lower desired quantity;
    //   'own'      — the response of our own /cart/change.js: the snapshot
    //                is replaced, desired quantities are left alone (a "+"
    //                clicked while the write was in flight stays a local
    //                addition);
    //   'consume'  — variant:add after our own submit: every addition is now
    //                in the cart, so desired = cart (pending removals keep
    //                the min rule).
    _applyCart(cart, mode) {
      if (this._dead) return;
      this._cartSeq++;
      var parsed = this._parseCart(cart);
      var old = this._cart;
      var self = this;
      var next = [];
      var seen = Object.create(null);
      function qtyIn(map, vid) { return map[vid] ? map[vid].quantity : 0; }

      this._items.forEach(function (item) {
        var vid = item.variantId;
        seen[vid] = true;
        var newQty = qtyIn(parsed.packs, vid);
        var desired;
        if (mode === 'own') desired = item.quantity;
        else if (self._pendingRemovals[vid]) desired = Math.min(item.quantity, newQty);
        else if (mode === 'consume') desired = newQty;
        else desired = newQty + Math.max(0, item.quantity - qtyIn(old.packs, vid));
        desired = self._clampDesired(vid, desired, newQty);
        if (desired > 0) next.push({ variantId: vid, quantity: desired });
      });
      Object.keys(parsed.packs).forEach(function (key) {
        var vid = parseInt(key, 10);
        if (seen[vid] || self._pendingRemovals[vid]) return;
        next.push({ variantId: vid, quantity: parsed.packs[vid].quantity });
      });

      this._items = next;
      this._cart = parsed;
      var firstApply = !this._cartApplied;
      this._cartApplied = true;
      if (mode === 'consume') this._lastIncremented = null;
      this._syncForm();
      // First application of the cart (SPEC §36.1): keep the speculative tier
      // batch when the real state has the same tier line sets.
      var keepTiers = firstApply && this._speculativeSignature !== null && this._tierSignature() === this._speculativeSignature;
      this._speculativeSignature = null;
      this._schedulePreview(keepTiers);
      this._renderAll();
    }

    // The tier line sets of the current state, as one string: what a tier
    // batch is scheduled for (SPEC §36.1).
    _tierSignature() {
      var parts = [];
      for (var n = 2; n <= FREE_EVERY; n++) parts.push(this._cacheKey(this._tierLines(n)));
      return parts.join('|');
    }

    // Cap applies to the desired quantity including the cart (SPEC §25.2);
    // what is already in the cart can never be clamped away.
    _clampDesired(vid, desired, cartQty) {
      if (desired <= cartQty) return desired;
      var product = this._byVariant[vid];
      var q = cartQty;
      while (q < desired && this._canAdd(product, q)) q++;
      return q;
    }

    _fetchCart(mode) {
      var self = this;
      var seq = ++this._cartSeq;
      return fetch(routeRoot() + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then(function (response) {
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return response.json();
        })
        .then(function (cart) {
          if (seq !== self._cartSeq) return; // a newer state landed meanwhile
          self._applyCart(cart, mode || 'external');
        })
        .catch(function (error) {
          console.warn('[liva-bundle-builder] cart fetch failed; keeping the current state', error);
        });
    }

    _cartQty(variantId) {
      var entry = this._cart.packs[variantId];
      return entry ? entry.quantity : 0;
    }

    _cartPacks() {
      var self = this;
      var total = 0;
      Object.keys(this._cart.packs).forEach(function (key) { total += self._cart.packs[key].quantity; });
      return total;
    }

    // Additions = desired − cart, per variant, > 0, in first-added order:
    // what Add to cart posts (SPEC §25.4).
    _additions() {
      var self = this;
      var lines = [];
      this._items.forEach(function (item) {
        var extra = item.quantity - self._cartQty(item.variantId);
        if (extra > 0) lines.push({ variantId: item.variantId, quantity: extra });
      });
      return lines;
    }

    _additionsTotal() {
      var total = 0;
      var lines = this._additions();
      for (var i = 0; i < lines.length; i++) total += lines[i].quantity;
      return total;
    }

    _hasPendingRemovals() {
      return Object.keys(this._pendingRemovals).length > 0;
    }

    _item(variantId) {
      for (var i = 0; i < this._items.length; i++) {
        if (this._items[i].variantId === variantId) return this._items[i];
      }
      return null;
    }

    _qty(variantId) {
      var item = this._item(variantId);
      return item ? item.quantity : 0;
    }

    _total() {
      var total = 0;
      for (var i = 0; i < this._items.length; i++) total += this._items[i].quantity;
      return total;
    }

    // The one write path for quantities: clamps to [0, cap] through
    // _canAdd, drops the item at 0, then saves, syncs the form, prices and
    // renders. Returns true when something changed.
    _setQty(variantId, wanted) {
      var product = this._byVariant[variantId];
      if (!product) return false;
      wanted = Math.max(0, Math.floor(Number(wanted) || 0));
      var item = this._item(variantId);
      var current = item ? item.quantity : 0;
      var next = current;
      if (wanted > current) {
        while (next < wanted && this._canAdd(product, next)) next++;
      } else {
        next = wanted;
      }
      if (next === current) return false;

      if (next === 0) {
        this._items.splice(this._items.indexOf(item), 1);
      } else if (item) {
        item.quantity = next;
      } else {
        this._items.push({ variantId: variantId, quantity: next });
      }
      if (next > current) this._lastIncremented = variantId;
      this._armRemoval(variantId, next);
      this._syncForm();
      this._schedulePreview();
      this._renderAll();
      return true;
    }

    /* ----------------------------------------------------------- removals
       "−" is live, "+" is batched (SPEC §25.3). A desired quantity below
       the cart baseline arms a debounce; when it fires, the variant joins
       the write chain (one /cart/change.js in flight, FIFO by first arm,
       the quantity read at send time). An increment back to the baseline
       cancels an armed or queued removal; an in-flight one is harmless
       because its response is applied in 'own' mode. Shopify splits a
       BXGY-freed pack into its own line: the write targets the PAID line's
       key with quantity = desired − free units (the free line is derived
       by the discount engine and must never be touched), and a second
       round converges when desired < free units. */

    _armRemoval(variantId, desired) {
      var self = this;
      if (this._removeTimers[variantId]) {
        clearTimeout(this._removeTimers[variantId]);
        delete this._removeTimers[variantId];
      }
      if (desired < this._cartQty(variantId)) {
        this._pendingRemovals[variantId] = true;
        this._removeTimers[variantId] = setTimeout(function () {
          delete self._removeTimers[variantId];
          self._enqueueWrite(variantId);
        }, REMOVE_DEBOUNCE_MS);
      } else {
        delete this._pendingRemovals[variantId];
        delete this._writeQueued[variantId];
      }
    }

    _clearRemovalTimers() {
      var self = this;
      Object.keys(this._removeTimers).forEach(function (vid) {
        clearTimeout(self._removeTimers[vid]);
      });
      this._removeTimers = Object.create(null);
    }

    // Fires every armed debounce now (submit with removals pending, SPEC §25.4).
    _flushRemovals() {
      var self = this;
      Object.keys(this._removeTimers).forEach(function (vid) {
        clearTimeout(self._removeTimers[vid]);
        delete self._removeTimers[vid];
        self._enqueueWrite(parseInt(vid, 10));
      });
    }

    _enqueueWrite(variantId) {
      if (this._writeQueued[variantId]) return;
      this._writeQueued[variantId] = true;
      var self = this;
      this._writeChain = this._writeChain
        .then(function () { return self._sendRemoval(variantId, 0); })
        .catch(function () { /* logged in _sendRemoval; the chain goes on */ });
    }

    _removalTarget(variantId) {
      var entry = this._cart.packs[variantId];
      if (!entry) return null;
      var desired = this._qty(variantId);
      if (desired >= entry.quantity) return null;
      return { key: entry.paidKey || entry.keys[0], quantity: Math.max(0, desired - entry.freeQuantity) };
    }

    _sendRemoval(variantId, round) {
      delete this._writeQueued[variantId];
      if (this._dead || !this._pendingRemovals[variantId]) return Promise.resolve();
      var target = this._removalTarget(variantId);
      if (!target) { delete this._pendingRemovals[variantId]; return Promise.resolve(); }

      var sections = [];
      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sections } }));
      var self = this;
      return fetch(routeRoot() + 'cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ id: target.key, quantity: target.quantity, sections: sections })
      })
        .then(function (response) {
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return response.json();
        })
        .then(function (cart) {
          if (self._dead) return;
          self._applyCart(cart, 'own');
          self._dispatchCartChange(cart);
          var stillPending = self._pendingRemovals[variantId] && self._qty(variantId) < self._cartQty(variantId);
          if (stillPending && round + 1 < WRITE_ROUNDS) return self._sendRemoval(variantId, round + 1);
          delete self._pendingRemovals[variantId];
          if (stillPending) self._applyCart(cart, 'external'); // rounds exhausted: back to the cart's truth
        })
        .catch(function (error) {
          console.warn('[liva-bundle-builder] cart removal failed; re-syncing from the cart', error);
          delete self._pendingRemovals[variantId];
          return self._fetchCart('external');
        });
    }

    // Tells the theme (header count, drawer, progress bar) about our write,
    // exactly like line-item-quantity does; our own cart:change listener
    // skips it.
    _dispatchCartChange(cart) {
      this._ownDispatch = true;
      try {
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'bundle:change', cart: cart } }));
      } finally {
        this._ownDispatch = false;
      }
      this._lastOwnDispatch = Date.now();
    }

    // pagehide: an armed "−" must not be lost to navigation; keepalive
    // sends it without waiting (no sections, nothing to render).
    _flushRemovalsKeepalive() {
      var self = this;
      Object.keys(this._removeTimers).forEach(function (key) {
        var vid = parseInt(key, 10);
        clearTimeout(self._removeTimers[key]);
        delete self._removeTimers[key];
        var target = self._removalTarget(vid);
        delete self._pendingRemovals[vid];
        if (!target) return;
        try {
          fetch(routeRoot() + 'cart/change.js', {
            method: 'POST', keepalive: true, credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify({ id: target.key, quantity: target.quantity })
          }).catch(function () { /* best effort */ });
        } catch (e) { /* best effort */ }
      });
    }

    /* -------------------------------------------------------------- setup */

    // Listeners are stored and removed explicitly in _unbind: the
    // addEventListener signal option is above the browser floor (SPEC §20.5)
    // and would be silently ignored there. Each handler is guarded so an
    // exception is logged instead of leaving the form half-updated.
    _bind() {
      var self = this;
      this._handlers = {
        click: guard(this, function (event) { self._onClick(event); }),
        scrollToPicker: guard(this, function (event) {
          // SPEC §44: the theme bar's button at 0 packs scrolls to the cards instead of submitting
          var t = event.target && event.target.closest ? event.target.closest('[data-bundle-scroll-to-picker]') : null;
          if (!t) return;
          event.preventDefault();
          self._scrollToPicker();
        }),
        variantAdd: guard(this, function (event) {
          // Successful add: the additions are in the cart now (SPEC §25.4).
          var cart = event.detail && event.detail.cart;
          self._scheduleRelease();    // SPEC §42: the add answered — release the busy hold after BUSY_MIN_MS
          if (cart && Array.isArray(cart.items)) self._applyCart(cart, 'consume');
          else self._fetchCart('consume');
        }),
        cartError: guard(this, function (event) {
          self._releaseBusy();        // SPEC §42: a 422 ends the busy hold
          self._onCartError(event.detail && event.detail.error);
        }),
        cartChange: guard(this, function (event) {
          // Drawer edits, the theme's post-add event, apps (SPEC §25.1).
          if (self._ownDispatch) return;
          var cart = event.detail && event.detail.cart;
          if (cart && Array.isArray(cart.items)) self._applyCart(cart, 'external');
          else self._fetchCart('external');
        }),
        submit: guard(this, function (event) { self._onSubmit(event); }),
        pageshow: guard(this, function (event) {
          if (!event.persisted) return; // bfcache: no cart:change fires, the cart may have changed
          self._clearRemovalTimers();
          self._pendingRemovals = Object.create(null);
          self._fetchCart('external');
        }),
        pagehide: guard(this, function () { self._flushRemovalsKeepalive(); })
      };

      this.addEventListener('click', this._handlers.click);
      document.addEventListener('click', this._handlers.scrollToPicker);
      document.addEventListener('cart:change', this._handlers.cartChange);
      document.addEventListener('submit', this._handlers.submit, true); // capture: before ProductForm's own listener
      window.addEventListener('pageshow', this._handlers.pageshow);
      window.addEventListener('pagehide', this._handlers.pagehide);

      var form = this._form();
      if (form) {
        form.addEventListener('variant:add', this._handlers.variantAdd);
        form.addEventListener('cart:error', this._handlers.cartError);
      }

      // SPEC §42: the theme's ProductForm removes aria-busy the moment
      // /cart/add.js answers (before variant:add). While a hold is on (our
      // submit, until variant:add / cart:error and BUSY_MIN_MS, never past
      // BUSY_CEILING_MS) the attribute is put back in the same task, so the
      // custom-button animation completes.
      if (window.MutationObserver) {
        this._busyObserver = new MutationObserver(guard(this, function (records) { self._onBusyMutation(records); }));
        var buttons = this._submitButtons();
        for (var b = 0; b < buttons.length; b++) this._observeBusy(buttons[b]);
      }
    }

    _unbind() {
      var h = this._handlers;
      if (!h) return;
      this.removeEventListener('click', h.click);
      document.removeEventListener('click', h.scrollToPicker);
      document.removeEventListener('cart:change', h.cartChange);
      document.removeEventListener('submit', h.submit, true);
      window.removeEventListener('pageshow', h.pageshow);
      window.removeEventListener('pagehide', h.pagehide);
      var form = this._form();
      if (form) {
        form.removeEventListener('variant:add', h.variantAdd);
        form.removeEventListener('cart:error', h.cartError);
      }
      if (this._busyObserver) { this._busyObserver.disconnect(); this._busyObserver = null; }
      this._handlers = null;
    }

    _form() {
      return this._formId ? document.getElementById(this._formId) : null;
    }

    // Click delegation (SPEC §24): "−" decrements; "+" (pill or stepper) and
    // any other click on a card increment. Disabled buttons never dispatch
    // click, and _setQty refuses anything _canAdd refuses.
    _onClick(event) {
      if (this._isBusy()) return; // an add is in flight: the posted lines must not drift
      var dec = event.target.closest('[data-bundle-dec]');
      if (dec) {
        var decId = parseInt(dec.getAttribute('data-bundle-dec'), 10);
        this._setQty(decId, this._qty(decId) - 1);
        return;
      }
      // Only the buttons act (SPEC §32): a click on the swatch, the name or
      // the card background does nothing (the card-body increment of §24.5
      // is gone).
      var inc = event.target.closest('[data-bundle-inc]');
      if (!inc || inc.disabled) return;
      var incId = parseInt(inc.getAttribute('data-bundle-inc'), 10);
      this._setQty(incId, this._qty(incId) + 1);
    }

    /* ------------------------------------------------------------- submit
       Capture-phase listener on document, so it runs before the theme's
       ProductForm handler on the form (SPEC §25.4). Three cases:
       nothing to add but packs in the cart -> open the cart, no request;
       additions with a removal pending -> flush the removals, wait for the
       write chain (and for the drawer's delayed swap), then re-dispatch
       submit; otherwise fall through to ProductForm. */

    _onSubmit(event) {
      var form = this._form();
      if (!form || event.target !== form || this._resubmitting) return;
      if (this._additions().length === 0) {
        event.preventDefault();
        event.stopPropagation();
        if (this._cartPacks() > 0) this._openCart();
        return;
      }
      if (!this._hasPendingRemovals()) { this._armBusyHold(); return; } // falls through to ProductForm (SPEC §42)

      event.preventDefault();
      event.stopPropagation();
      this._flushRemovals();
      this._setBusy(true);
      var self = this;
      this._writeChain.then(function () {
        var wait = Math.max(0, DRAWER_SWAP_DELAY_MS - (Date.now() - self._lastOwnDispatch));
        return new Promise(function (resolve) { setTimeout(resolve, wait); });
      }).then(function () {
        self._setBusy(false);
        var f = self._form();
        if (self._dead || !f) return;
        if (self._additions().length === 0) { self._syncButtons(); return; }
        self._resubmitting = true;
        self._armBusyHold();
        try {
          f.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        } finally {
          self._resubmitting = false;
        }
      });
    }

    _openCart() {
      var tv = window.themeVariables && window.themeVariables.settings;
      var drawer = document.getElementById('cart-drawer');
      if (tv && tv.cartType === 'drawer' && drawer && typeof drawer.show === 'function') {
        drawer.show();
      } else {
        window.location.href = this._data.cartUrl || (routeRoot() + 'cart');
      }
    }

    // The theme marks its submit buttons aria-busy while an add is in
    // flight; the wait for pending removals reuses that state plus the
    // builder's own spinner class.
    _isBusy() {
      if (this._busy) return true;
      var buttons = this._submitButtons();
      for (var i = 0; i < buttons.length; i++) {
        if (buttons[i].getAttribute('aria-busy') === 'true') return true;
      }
      return false;
    }

    _setBusy(on) {
      on = !!on;
      if (this._busy === on) return;
      this._busy = on;
      var buttons = this._submitButtons();
      for (var i = 0; i < buttons.length; i++) {
        if (on) buttons[i].setAttribute('aria-busy', 'true');
        else buttons[i].removeAttribute('aria-busy');
      }
      this._syncLoadingClasses();
    }

    /* ----------------------------------------------- buy button feedback (§42)
       Impact's custom-button (theme.js) wraps the button's children in a
       content <div> at upgrade and appends its loader <span>; aria-busy
       slides the content out and the loader in, and back. The builder only
       ever writes labels INTO that content element, never the button's own
       children, so the loader survives every state. Busy: the theme sets
       aria-busy on submit; the builder holds it (re-sets it when the theme
       drops it on the response) until variant:add / cart:error and at least
       BUSY_MIN_MS, and never past BUSY_CEILING_MS after the submit (§42.1).
       Nothing is written to the button between the submit and the theme's
       slide-back: the same label slides back, like every other buy button
       (§42.1 — no confirmation text, no class). */
    _buttonContent(el) {
      var div = null;
      try { div = el.contentElement; } catch (e) { div = null; } // the custom-button's getter
      if (div && div.nodeType === 1 && el.contains(div)) return div;
      return el; // not an upgraded custom-button: the button itself
    }

    _observeBusy(el) {
      if (!this._busyObserver || el._livaBusyObserved) return;
      el._livaBusyObserved = true;
      this._busyObserver.observe(el, { attributes: true, attributeFilter: ['aria-busy'] });
    }

    _onBusyMutation(records) {
      if (!this._holdBusy || this._dead) return;
      for (var i = 0; i < records.length; i++) {
        var el = records[i].target;
        if (el.getAttribute('aria-busy') !== 'true') el.setAttribute('aria-busy', 'true');
      }
    }

    _armBusyHold() {
      var self = this;
      this._submitAt = Date.now();
      this._holdBusy = true;
      if (this._releaseTimer) { clearTimeout(this._releaseTimer); this._releaseTimer = null; }
      if (this._ceilingTimer) clearTimeout(this._ceilingTimer);
      this._ceilingTimer = setTimeout(function () { self._ceilingTimer = null; if (!self._dead) self._releaseBusy(); }, BUSY_CEILING_MS);
    }

    // The add answered (variant:add): release at submit + BUSY_MIN_MS, or now.
    _scheduleRelease() {
      var self = this;
      if (!this._holdBusy) return;
      var wait = Math.max(0, this._submitAt + BUSY_MIN_MS - Date.now());
      if (this._releaseTimer) clearTimeout(this._releaseTimer);
      this._releaseTimer = setTimeout(function () { self._releaseTimer = null; if (!self._dead) self._releaseBusy(); }, wait);
    }

    _releaseBusy() {
      this._holdBusy = false;
      if (this._releaseTimer) { clearTimeout(this._releaseTimer); this._releaseTimer = null; }
      if (this._ceilingTimer) { clearTimeout(this._ceilingTimer); this._ceilingTimer = null; }
      if (this._busy) return; // the builder's own busy (pending removals) still owns the attribute
      var buttons = this._submitButtons();
      for (var i = 0; i < buttons.length; i++) buttons[i].removeAttribute('aria-busy');
    }

    /* ----------------------------------------------------------- 422 (§5.6)
       /cart/add.js refused the add (stock changed since page load). The
       theme's ProductForm dispatches cart:error with the response
       description and buy-buttons shows it as a banner; here the flavour's
       card is marked so the customer sees WHICH pack is gone. Primary path:
       the description names the product ("The product 'HYDRATION - Citrón'
       is already sold out."), so match every chosen product's full title.
       Fallback (SPEC §19): no title matches -> mark the last incremented
       flavour (else the last item); a 422 right after an add is almost
       always the last pack chosen. The product is treated as sold out for
       the rest of the session ("+" disabled, "−" still works), the other
       items stay, and the button is disabled until its quantity is 0. */

    _onCartError(message) {
      var text = String(message || '').toLowerCase();
      var matched = [];
      var additions = this._additions(); // only what was posted can have been refused (SPEC §25.6)
      var added = Object.create(null);
      for (var i = 0; i < additions.length; i++) {
        var vid = additions[i].variantId;
        added[vid] = true;
        var product = this._byVariant[vid];
        var title = product && product.fullTitle ? String(product.fullTitle).toLowerCase() : '';
        if (title && text.indexOf(title) !== -1) matched.push(vid);
      }
      if (matched.length === 0 && additions.length > 0) {
        var fallback = added[this._lastIncremented] ? this._lastIncremented : additions[additions.length - 1].variantId;
        matched.push(fallback);
        console.warn('[liva-bundle-builder] 422 without a matching product title; marking the last added flavour', message);
      }
      for (var j = 0; j < matched.length; j++) {
        this._unavailable[matched[j]] = true;
        var p = this._byVariant[matched[j]];
        if (p) p.available = false;
      }
      this._renderAll();
    }

    // A refused flavour blocks the button only while it is still being added.
    _hasUnavailable() {
      var additions = this._additions();
      for (var i = 0; i < additions.length; i++) {
        if (this._unavailable[additions[i].variantId]) return true;
      }
      return false;
    }

    /* -------------------------------------------------------- availability */

    // Stock cap (deny policy) capped again by the theme setting (SPEC §24).
    _cappedCount(product) {
      var cap = this._maxPerProduct;
      if (product.inventoryPolicy === 'deny' && typeof product.inventoryQuantity === 'number') {
        cap = Math.min(cap, Math.max(0, product.inventoryQuantity));
      }
      return cap;
    }

    // The one availability rule (SPEC §10, §21): one more pack of a product
    // can be added while it is available and the packs already chosen
    // (`count`, default the current quantity) number fewer than its cap.
    // Used by add, restore, cards and prefetch.
    _canAdd(product, count) {
      if (!product || !product.available) return false;
      if (typeof count !== 'number') count = this._qty(product.variantId);
      return count < this._cappedCount(product);
    }

    // Full (undiscounted) sum of the desired cart: desired packs at list
    // price plus the non-pack lines' original line prices (SPEC §25.5).
    _fullTotal() {
      var total = 0;
      for (var i = 0; i < this._items.length; i++) {
        var product = this._byVariant[this._items[i].variantId];
        if (product) total += product.price * this._items[i].quantity;
      }
      return total + this._othersTotal();
    }

    _othersTotal() {
      var total = 0;
      for (var i = 0; i < this._cart.others.length; i++) total += this._cart.others[i].originalLinePrice;
      return total;
    }

    // Desired pack lines per variant in first-added order:
    // [{ variantId, quantity }]. `plusVariantId` adds one pack of that
    // variant (prefetch of the next state).
    _consolidatedLines(plusVariantId) {
      var lines = this._items.map(function (item) { return { variantId: item.variantId, quantity: item.quantity }; });
      if (plusVariantId != null) {
        var found = null;
        for (var i = 0; i < lines.length; i++) if (lines[i].variantId === plusVariantId) found = lines[i];
        if (found) found.quantity++;
        else lines.push({ variantId: plusVariantId, quantity: 1 });
      }
      return lines;
    }

    // What the engine prices (SPEC §25.5): the desired packs plus every
    // non-pack line already in the cart, so the total matches the drawer.
    _previewLines(plusVariantId) {
      var lines = this._consolidatedLines(plusVariantId);
      for (var i = 0; i < this._cart.others.length; i++) {
        lines.push({ variantId: this._cart.others[i].variantId, quantity: this._cart.others[i].quantity });
      }
      return lines;
    }

    // Money format (SPEC §18.4, §21): the theme's own settings first
    // (js-variables.liquid); the blob's money_with_currency_format is the
    // only fallback. `short` picks the format without the currency code
    // (card prices).
    _moneyFormat(short) {
      var tv = window.themeVariables && window.themeVariables.settings;
      var format = tv && (short ? tv.moneyFormat : tv.moneyWithCurrencyFormat);
      return format || this._data.moneyFormat;
    }

    /* ------------------------------------------------------------ preview
       Storefront API cartCreate with the full desired cart (SPEC §7, §18.4,
       §19.8, §25.5). On every quantity change the price is set synchronously: the
       cached engine result for this line set when there is one, otherwise
       an optimistic placeholder (sum of list prices, minus the cheapest
       pack per every 4 — the 3+1 the ladder promises). A cache miss sends
       the request after a short debounce (previous request aborted, stale
       responses dropped); the engine answer overwrites the placeholder and
       is the source of truth. Likely next states are prefetched into the
       cache at low priority. The spinner shows only when no engine total
       has arrived SPINNER_DELAY_MS after the user's action, never on a
       cache hit. If the engine fails, the optimistic total stays. */

    // Cancels the pending user request and a tier batch not yet sent; an
    // in-flight tier batch is aborted by the _scheduleTiers that every
    // _schedulePreview call makes right after this, and by _abortPrefetches(true).
    _cancelPreview(keepTiers) {
      if (this._previewTimer) { clearTimeout(this._previewTimer); this._previewTimer = null; }
      if (!keepTiers && this._tierTimer) { clearTimeout(this._tierTimer); this._tierTimer = null; }
      if (this._spinnerTimer) { clearTimeout(this._spinnerTimer); this._spinnerTimer = null; }
      if (this._previewAbort) { this._previewAbort.abort(); this._previewAbort = null; }
    }

    // Decides what the price UI shows for the current items. Callers render
    // afterwards (_renderAll / _renderPreview); nothing is rendered here.
    // `keepTiers` (first cart application with an unchanged tier signature,
    // SPEC §36.1): the speculative tier batch stays; only the ATC preview and
    // the prefetch are (re)scheduled.
    _schedulePreview(keepTiers) {
      this._cancelPreview(keepTiers);
      this._setLoading(false);
      this._previewPending = false;

      if (!keepTiers) this._scheduleTiers();

      if (this._total() === 0) {
        this._preview = null;
        this._queuePrefetch();
        return;
      }

      var lines = this._previewLines();
      var cached = this._cacheGet(lines);
      if (cached) {
        this._preview = cached;       // engine truth, synchronously
        this._queuePrefetch();
        return;
      }

      this._preview = this._optimisticPreview();
      this._previewPending = true;
      var self = this;
      this._previewTimer = setTimeout(function () {
        self._previewTimer = null;
        self._runPreview(lines);
      }, PREVIEW_DEBOUNCE_MS);
      this._spinnerTimer = setTimeout(function () {
        self._spinnerTimer = null;
        if (self._previewPending) self._setLoading(true);
      }, SPINNER_DELAY_MS);
    }

    // Placeholder shown until the engine answers: list prices, minus the
    // cheapest pack for every 4 packs. No percentage is ever assumed (SPEC §19.1).
    _optimisticPreview() {
      var packs = this._optimisticPacks(this._consolidatedLines());
      // non-pack lines at their original price (the engine prices a fresh cart, no codes)
      return { total: packs.total + this._othersTotal(), freeByPrice: packs.freeByPrice, optimistic: true };
    }

    // The cheapest-free estimate for pack lines only: { total, freeByPrice }.
    // Shared by the ATC placeholder and the ladder's tier-4 figure (SPEC §28.2).
    _optimisticPacks(lines) {
      var prices = [];
      for (var i = 0; i < lines.length; i++) {
        var product = this._byVariant[lines[i].variantId];
        if (!product) continue;
        for (var q = 0; q < lines[i].quantity; q++) prices.push(product.price);
      }
      prices.sort(function (a, b) { return a - b; });
      var free = Math.floor(prices.length / FREE_EVERY);
      var total = 0;
      var freeByPrice = {};
      for (var j = 0; j < prices.length; j++) {
        if (j < free) freeByPrice[prices[j]] = (freeByPrice[prices[j]] || 0) + 1;
        else total += prices[j];
      }
      return { total: total, freeByPrice: freeByPrice };
    }

    /* -------------------------------------------------------- ladder tiers
       The per-pack figures under ladder points 2, 3 and 4 come from the
       pricing engine (SPEC §28.2), never from client-side discount maths:
       tier n = the first n desired units in first-added order, padded with
       the page product up to n (so on load, or with nothing chosen, tier n
       is n × the page product); tier 4 = the full desired mix once it
       holds more than 4 units. Per pack = engine total ÷ units, in integer
       cents like the Liquid figure, interpolated into the locale template
       (blob below2..4, "{price} / bal.") with the theme's money format.
       A figure equal to point 1's list price (in cents) is hidden — the
       span is blanked, the row keeps its height — so 2 and 3 only show a
       real discount and 4 always shows in a 3+1 mix. Tiers 2 and 3 are
       never estimated (their discount is a Functions amount the client
       cannot know): the Liquid / last engine value stands until the engine
       answers. Tier 4 may show the cheapest-free estimate
       meanwhile, as the ATC does. Cache hits (shared with the ATC preview)
       write synchronously; misses fetch at low priority after the preview
       debounce, and a stale answer never overwrites a newer state. */

    _tierLines(n) {
      var units = [];
      for (var i = 0; i < this._items.length; i++) {
        for (var q = 0; q < this._items[i].quantity; q++) units.push(this._items[i].variantId);
      }
      if (!(n === FREE_EVERY && units.length > n)) {
        units = units.slice(0, n);
        while (units.length < n) units.push(this._currentProduct.variantId);
      }
      var lines = [];
      for (var u = 0; u < units.length; u++) {
        var found = null;
        for (var l = 0; l < lines.length; l++) if (lines[l].variantId === units[u]) found = lines[l];
        if (found) found.quantity++;
        else lines.push({ variantId: units[u], quantity: 1 });
      }
      return lines;
    }

    // Tier batch (SPEC §35.2): the misses of one change form one batch with
    // one AbortController; the next _scheduleTiers aborts the previous batch
    // (queued jobs dropped, in-flight fetches aborted) and _tierSeq stays as
    // the second guard, so a superseded batch never writes. The jobs run
    // through the prefetch queue, ahead of plain prefetches, under its
    // in-flight cap: the user's preview plus at most PREFETCH_CONCURRENCY
    // queued fetches = 3 engine requests in flight at any time.
    _scheduleTiers() {
      if (this._tierTimer) { clearTimeout(this._tierTimer); this._tierTimer = null; }
      var seq = ++this._tierSeq;
      this._abortTierBatch();
      var self = this;
      var pending = [];
      for (var n = 2; n <= FREE_EVERY; n++) {
        var lines = this._tierLines(n);
        var units = 0;
        for (var i = 0; i < lines.length; i++) units += lines[i].quantity;
        var cached = this._cacheGet(lines);
        if (cached) {
          this._writeTier(n, cached.total, units, false);
        } else {
          if (n === FREE_EVERY) this._writeTier(n, this._optimisticPacks(lines).total, units, true);
          pending.push({ n: n, lines: lines, units: units });
        }
      }
      if (!pending.length || !this._data.storefrontToken) return;
      this._tierTimer = setTimeout(function () {
        self._tierTimer = null;
        if (seq !== self._tierSeq) return;
        var controller = new AbortController();
        self._tierAbort = controller;
        pending.forEach(function (job) {
          self._queueEngineJob(job.lines, { n: job.n, units: job.units, seq: seq }, controller);
        });
        self._drainPrefetch();
      }, PREVIEW_DEBOUNCE_MS);
    }

    // Drops the queued jobs of the current tier batch and aborts its
    // in-flight fetches. A prefetch job that merely carries tier writers
    // keeps running (its writers are seq-checked on completion).
    _abortTierBatch() {
      var self = this;
      this._prefetchQueue = this._prefetchQueue.filter(function (job) {
        if (!job.tierBatch) return true;
        delete self._prefetchKeys[job.key];
        return false;
      });
      if (this._tierAbort) { this._tierAbort.abort(); this._tierAbort = null; }
    }

    // A tier job for `lines`: attaches its writer to a queued or in-flight
    // job with the same key when there is one (one request per line set),
    // else queues a new job ahead of the plain prefetches, bound to the
    // batch's AbortController.
    _queueEngineJob(lines, tier, controller) {
      var key = this._cacheKey(lines);
      var existing = null;
      var all = this._prefetchQueue.concat(this._prefetchInFlight);
      for (var i = 0; i < all.length; i++) if (all[i].key === key) { existing = all[i]; break; }
      if (existing) { existing.tiers.push(tier); return; }
      this._prefetchKeys[key] = true;
      this._prefetchQueue.unshift({ key: key, lines: lines, controller: null, tiers: [tier], tierBatch: controller });
    }

    // A tier's figure is shown only when it differs from point 1's list price
    // (compared in cents): 3+1 always shows under 4; 2 and 3 appear only
    // while a discount exists at that tier. The span stays (row height).
    _writeTier(n, total, units, optimistic) {
      var perPack = Math.floor(total / units);
      var shown = perPack !== this._currentProduct.price;
      this._tiers[n] = { units: units, total: total, perPack: perPack, optimistic: optimistic, shown: shown };
      var span = this.querySelector('[data-bundle-below="' + n + '"]');
      var template = this._strings['below' + n];
      if (!span || !template) return;
      var text = shown ? interpolate(template, { price: formatMoney(perPack, this._moneyFormat()) }) : '';
      if (span.textContent !== text) span.textContent = text;
      // No-price layout: the class lifts the ribbon onto the price row (CSS);
      // dropped in the same write when a discount reappears at that tier.
      var point = span.closest('[data-bundle-point]');
      if (point) point.classList.toggle('bundle-ladder__point--no-price', !shown);
    }

    _runPreview(lines) {
      var self = this;
      this._abortPrefetches();        // the user's own request wins
      var seq = ++this._previewSeq;
      var controller = new AbortController();
      this._previewAbort = controller;

      this._fetchEngine(lines, controller.signal, false)
        .then(function (result) {
          if (seq !== self._previewSeq) return; // superseded
          self._cacheSet(lines, result);
          self._applyPreviewResult(result);
        })
        .catch(function (error) {
          if (error && error.name === 'AbortError') return;
          if (seq !== self._previewSeq) return;
          console.warn('[liva-bundle-builder] price preview failed; keeping the optimistic total', error);
          self._applyPreviewResult(null);
        });
    }

    // One engine round trip -> normalized { total, freeByPrice }. Rejects on
    // HTTP, GraphQL or userErrors; the caller decides what to show.
    _fetchEngine(lines, signal, lowPriority) {
      var token = this._data.storefrontToken;
      if (!token) {
        return Promise.reject(new Error('storefront token missing; price preview disabled'));
      }
      var gqlLines = lines.map(function (line) {
        return { merchandiseId: 'gid://shopify/ProductVariant/' + line.variantId, quantity: line.quantity };
      });
      var version = this._data.apiVersion || '2026-07';
      var url = 'https://' + this._data.shopDomain + '/api/' + version + '/graphql.json';
      var self = this;
      var init = {
        method: 'POST',
        signal: signal,
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token
        },
        body: JSON.stringify({
          query: PREVIEW_QUERY,
          variables: { lines: gqlLines, country: this._data.country }
        })
      };
      if (lowPriority) init.priority = 'low'; // fetch priority hint; ignored where unsupported

      return fetch(url, init)
        .then(function (response) {
          if (!response.ok) throw new Error('HTTP ' + response.status);
          return response.json();
        })
        .then(function (payload) {
          var result = payload && payload.data && payload.data.cartCreate;
          if (!result || (payload.errors && payload.errors.length)) {
            throw new Error(JSON.stringify(payload.errors || payload));
          }
          if (result.userErrors && result.userErrors.length) {
            throw new Error(JSON.stringify(result.userErrors));
          }
          return self._parsePreview(result.cart);
        });
    }

    // Free-unit mapping (SPEC §7.3, refined in §19.8): per variant, sum the
    // discount allocations over every line holding it (the engine splits a
    // freed unit into its own line), divide by the unit price, round, clamp;
    // then fold the free units onto the variant's list PRICE. The cheapest-
    // free note reads the resulting counts.
    _parsePreview(cart) {
      var total = toCents(cart.cost.totalAmount.amount);
      var perVariant = {};
      var nodes = (cart.lines && cart.lines.nodes) || [];
      for (var i = 0; i < nodes.length; i++) {
        var node = nodes[i];
        var gid = node.merchandise && node.merchandise.id;
        if (!gid) continue;
        var variantId = parseInt(gid.split('/').pop(), 10);
        if (!perVariant[variantId]) perVariant[variantId] = { quantity: 0, discounted: 0, perUnit: 0 };
        var entry = perVariant[variantId];
        entry.quantity += node.quantity;
        entry.perUnit = toCents(node.cost.amountPerQuantity.amount) || entry.perUnit;
        var allocations = node.discountAllocations || [];
        for (var j = 0; j < allocations.length; j++) {
          entry.discounted += toCents(allocations[j].discountedAmount.amount);
        }
      }
      var freeByPrice = {};
      var self = this;
      Object.keys(perVariant).forEach(function (variantId) {
        var e = perVariant[variantId];
        if (!e.perUnit) return;
        var free = Math.round(e.discounted / e.perUnit);
        free = Math.max(0, Math.min(e.quantity, free));
        if (free <= 0) return;
        var product = self._byVariant[variantId];
        if (!product) return; // a discounted non-pack line is not a free pack
        freeByPrice[product.price] = (freeByPrice[product.price] || 0) + free;
      });
      return { total: total, freeByPrice: freeByPrice };
    }

    _applyPreviewResult(result) {
      this._previewAbort = null;
      this._previewPending = false;
      if (this._spinnerTimer) { clearTimeout(this._spinnerTimer); this._spinnerTimer = null; }
      this._setLoading(false);
      if (result) this._preview = result;   // engine wins; on failure the optimistic total stays
      this._renderPreview();
      if (result) this._queuePrefetch();
    }

    /* -------------------------------------------------------------- cache
       Keyed by the sorted variant ids with quantities (SPEC §19.8). A miss
       falls back to the same line set's price signature: the discount
       engine's answer for these packs depends on their list prices only
       (the 3+1 automatic discount covers every group product), so one
       prefetched representative per price point serves every flavour at
       that price. Drop _cacheByPrice if a product-specific discount is
       ever introduced. */

    _cacheKey(lines) {
      return lines.map(function (l) { return l.variantId + 'x' + l.quantity; }).sort().join(',');
    }

    // Pack prices (numeric sort) and, after '|', the non-pack lines by
    // variant and quantity: a cache hit must not ignore them (SPEC §25.5).
    _priceKey(lines) {
      var self = this;
      var parts = [];
      var others = [];
      lines.forEach(function (l) {
        var product = self._byVariant[l.variantId];
        if (!product) { others.push(l.variantId + 'x' + l.quantity); return; }
        for (var i = 0; i < l.quantity; i++) parts.push(product.price);
      });
      return parts.sort(function (a, b) { return a - b; }).join(',') + '|' + others.sort().join(',');
    }

    _cacheGet(lines) {
      return this._cache[this._cacheKey(lines)] || this._cacheByPrice[this._priceKey(lines)] || null;
    }

    _cacheSet(lines, result) {
      this._cache[this._cacheKey(lines)] = result;
      this._cacheByPrice[this._priceKey(lines)] = result;
    }

    /* ----------------------------------------------------------- prefetch
       After each quantity change (and on init, for the empty -> first-click
       states): for every distinct price point among available products,
       the state "current items + one pack of that price" is fetched into
       the cache if missing. One low-priority queue, at most
       PREFETCH_CONCURRENCY in flight, aborted whenever the user's own
       request starts. */

    _queuePrefetch() {
      if (!this._data.storefrontToken) return;
      var self = this;
      this._pricePointRepresentatives().forEach(function (product) {
        var lines = self._previewLines(product.variantId);
        var key = self._cacheKey(lines);
        if (self._cacheGet(lines) || self._prefetchKeys[key]) return;
        self._prefetchKeys[key] = true;
        self._prefetchQueue.push({ key: key, lines: lines, controller: null, tiers: [], tierBatch: null });
      });
      this._drainPrefetch();
    }

    // One product per distinct price: prefer a flavour already chosen
    // (repeats are common), then the page's product, then the first
    // available one. Sold-out and capped products are skipped.
    _pricePointRepresentatives() {
      var self = this;
      var byPrice = {};
      var order = [];
      function consider(product, rank) {
        if (!self._canAdd(product)) return;
        var current = byPrice[product.price];
        if (!current) order.push(product.price);
        if (!current || rank < current.rank) byPrice[product.price] = { product: product, rank: rank };
      }
      for (var i = this._items.length - 1; i >= 0; i--) consider(this._byVariant[this._items[i].variantId], 0);
      consider(this._currentProduct, 1);
      (this._data.groups || []).forEach(function (group) {
        (group.products || []).forEach(function (product) { consider(product, 2); });
      });
      return order.map(function (price) { return byPrice[price].product; });
    }

    _drainPrefetch() {
      var self = this;
      while (this._prefetchInFlight.length < PREFETCH_CONCURRENCY && this._prefetchQueue.length > 0) {
        (function (job) {
          // A tier job shares its batch's controller (SPEC §35.2); a plain
          // prefetch gets its own, aborted when the user's request starts.
          job.controller = job.tierBatch || new AbortController();
          self._prefetchInFlight.push(job);
          self._fetchEngine(job.lines, job.controller.signal, true)
            .then(function (result) {
              self._cacheSet(job.lines, result);
              for (var t = 0; t < job.tiers.length; t++) {
                if (job.tiers[t].seq === self._tierSeq) self._writeTier(job.tiers[t].n, result.total, job.tiers[t].units, false);
              }
              // The user may be waiting for exactly this state right now.
              if (self._previewPending && self._cacheKey(self._previewLines()) === job.key) {
                self._cancelPreview();
                self._applyPreviewResult(result);
              }
            })
            .catch(function () { /* best effort; the next change retries (the standing tier figure stays) */ })
            .then(function () { self._prefetchDone(job); });
        })(this._prefetchQueue.shift());
      }
    }

    _prefetchDone(job) {
      var idx = this._prefetchInFlight.indexOf(job);
      if (idx !== -1) this._prefetchInFlight.splice(idx, 1);
      delete this._prefetchKeys[job.key];
      this._drainPrefetch();
    }

    // The user's own request wins over plain prefetches. Jobs carrying tier
    // writers survive (the ladder figures must settle even during a burst);
    // `all` (disconnect, fail-safe) aborts those too, through their batch.
    _abortPrefetches(all) {
      var self = this;
      var keep = [];
      this._prefetchQueue.forEach(function (job) {
        if (!all && job.tiers.length) keep.push(job);
        else delete self._prefetchKeys[job.key];
      });
      this._prefetchQueue = keep;
      this._prefetchInFlight.slice().forEach(function (job) {
        if (!all && job.tiers.length) return;
        if (job.controller) job.controller.abort();
      });
      if (all) this._abortTierBatch();
    }

    /* ------------------------------------------------------- price render */

    _pricesDiffer() {
      var first = null;
      for (var i = 0; i < this._items.length; i++) {
        var product = this._byVariant[this._items[i].variantId];
        if (!product) continue;
        if (first === null) first = product.price;
        else if (product.price !== first) return true;
      }
      return false;
    }

    _freeUnits() {
      var freeByPrice = (this._preview && this._preview.freeByPrice) || {};
      var count = 0;
      Object.keys(freeByPrice).forEach(function (key) { count += freeByPrice[key]; });
      return count;
    }

    // Everything that depends on the price state.
    _renderPreview() {
      this._renderHint();
      this._renderPrice();
      this._syncButtons();
    }

    _setLoading(on) {
      on = !!on;
      if (this._loading === on) return;
      this._loading = on;
      this._syncLoadingClasses();
    }

    // The one spinner rule (SPEC §19.8.5, §21): price list and Add to cart
    // button show is-loading only while the engine is late, with at least
    // one pack. Called from _setLoading, _renderPrice and _syncButtons so
    // the class never depends on which render ran last.
    _syncLoadingClasses() {
      var on = (!!this._loading || !!this._busy) && !this._dead && this._total() > 0;
      var list = this._priceList();
      if (list) list.classList.toggle('is-loading', on);
      var buttons = this._submitButtons();
      for (var i = 0; i < buttons.length; i++) {
        buttons[i].classList.toggle('is-loading', on);
      }
    }

    // Top-right price block (SPEC §18.4a, §19.10, §19.11): the theme's own
    // <price-list> in .product-info__price is reused. The Liquid markup is
    // cached on first touch and restored byte for byte by the fail-safe /
    // disconnect (`restore`); otherwise <sale-price> receives the total
    // (keeping its sr-only label) and a sibling <compare-at-price
    // class="text-subdued line-through"> is added/removed for the struck
    // full sum. A Liquid-rendered compare-at-price (product on sale) is
    // hidden while the builder owns the list. sale-price/compare-at-price
    // are plain elements in this theme (no custom element, no
    // variant:change reaction), and nothing on these single-variant PDPs
    // fires product:rerender, so nothing else rewrites the list.
    _priceList() {
      var root = this.closest('.product-info');
      var block = root && root.querySelector('.product-info__price');
      return block ? block.querySelector('price-list') : null;
    }

    _renderPrice(restore) {
      var list = this._priceList();
      var sale = list && list.querySelector('sale-price');
      if (!sale) return;
      if (!this._priceCache) {
        var sr = sale.querySelector('.sr-only');
        this._priceCache = {
          saleHtml: sale.innerHTML,
          srHtml: sr ? sr.outerHTML : '',
          liquidCompare: list.querySelector('compare-at-price')
        };
      }
      var cache = this._priceCache;
      var compare = list.querySelector('[data-bundle-compare]');

      if (restore) {
        // The Liquid markup, untouched (fail-safe, disconnect).
        if (sale.innerHTML !== cache.saleHtml) sale.innerHTML = cache.saleHtml;
        if (cache.liquidCompare) cache.liquidCompare.hidden = false;
        if (compare && compare.parentNode) compare.parentNode.removeChild(compare);
        this._syncLoadingClasses();
        return;
      }

      // 0 packs (SPEC §19.10): the page product's single-pack price, no
      // strikethrough; from 1 pack the optimistic / engine total as usual.
      var display = this._total() > 0
        ? this._priceDisplay()
        : { total: formatMoney(this._currentProduct.price, this._moneyFormat()), full: null };
      if (!display) return;

      sale.innerHTML = cache.srHtml + escapeHtml(display.total);
      if (cache.liquidCompare) cache.liquidCompare.hidden = true;
      if (display.full) {
        if (!compare) {
          compare = document.createElement('compare-at-price');
          compare.className = 'text-subdued line-through';
          compare.setAttribute('data-bundle-compare', '');
          sale.parentNode.insertBefore(compare, sale.nextSibling);
        }
        compare.innerHTML = '<span class="sr-only">' + escapeHtml(this._strings.srRegularPrice) + '</span>' + escapeHtml(display.full);
      } else if (compare && compare.parentNode) {
        compare.parentNode.removeChild(compare);
      }
      this._syncLoadingClasses();
    }

    // Put the Liquid price back (fail-safe, disconnect).
    _restorePrice() {
      try { this._renderPrice(true); } catch (error) { /* noop */ }
    }

    // Shared by the price block and the button: { total, full|null }. With
    // packs there is always a total — engine (cached or fresh) or the
    // optimistic placeholder (SPEC §19.8).
    _priceDisplay() {
      var preview = this._preview;
      if (!preview || typeof preview.total !== 'number') return null;
      var money = this._moneyFormat();
      var full = this._fullTotal();
      return {
        total: formatMoney(preview.total, money),
        full: preview.total < full ? formatMoney(full, money) : null
      };
    }

    /* --------------------------------------------------------------- form */

    // The 1-pack inputs are disabled (excluded from FormData, SPEC §5.6) and
    // the ADDITIONS (desired − cart, SPEC §25.4) are written as
    // items[n][id] / items[n][quantity]; what is already in the cart is
    // never posted again.
    _syncForm() {
      if (this._dead) return;
      var form = this._form();
      if (!form) return;

      var idInput = form.elements.namedItem('id');
      var quantityInput = document.getElementById(this._formId + '-quantity');
      if (idInput) idInput.disabled = true;
      if (quantityInput) quantityInput.disabled = true;

      var holder = this.querySelector('[data-bundle-inputs]');
      if (!holder) {
        holder = document.createElement('div');
        holder.setAttribute('data-bundle-inputs', '');
        holder.hidden = true;
        this.appendChild(holder);
      }
      holder.innerHTML = '';

      var lines = this._additions();
      for (var j = 0; j < lines.length; j++) {
        var vidAttr = escapeHtml(String(parseInt(lines[j].variantId, 10)));
        var qtyAttr = escapeHtml(String(parseInt(lines[j].quantity, 10)));
        holder.insertAdjacentHTML('beforeend',
          '<input type="hidden" name="items[' + j + '][id]" value="' + vidAttr + '" form="' + escapeHtml(this._formId) + '">' +
          '<input type="hidden" name="items[' + j + '][quantity]" value="' + qtyAttr + '" form="' + escapeHtml(this._formId) + '">');
      }

      this._syncButtons();
    }

    _submitButtons() {
      var form = this._form();
      var buttons = [];
      if (!form) return buttons;
      var elements = form.elements;
      for (var i = 0; i < elements.length; i++) {
        if (elements[i].tagName === 'BUTTON' && elements[i].type === 'submit') buttons.push(elements[i]);
      }
      return buttons;
    }

    /* ---------------------------------------- theme sticky bar (SPEC §44)
       Impact's product-quick-add bar holds submit buttons associated with
       the form through form= (two: phone and desktop). They are found once
       by their ancestor, not re-derived by type, because at 0 packs they
       become type="button" with data-bundle-scroll-to-picker: a tap then
       scrolls the page so the true buy button lands on the viewport bottom
       (its CSS-sticky slot) with the cards above it, and focuses the first
       enabled card button — no submit, no request. From 1 pack they are
       submit buttons again, mirrored like every other one. The true buy
       button stays disabled at 0 packs (SPEC §25). */
    _themeBarButtons() {
      if (this._barButtons) {
        for (var k = 0; k < this._barButtons.length; k++) {
          if (!this._barButtons[k].isConnected) { this._barButtons = null; break; } // the bar was re-rendered: find again
        }
      }
      if (!this._barButtons) {
        var form = this._form();
        var found = [];
        if (form) {
          var elements = form.elements;
          for (var i = 0; i < elements.length; i++) {
            var el = elements[i];
            if (el.tagName === 'BUTTON' && (el.type === 'submit' || el.hasAttribute('data-bundle-scroll-to-picker')) && el.closest && el.closest('product-quick-add')) found.push(el);
          }
        }
        this._barButtons = found;
      }
      return this._barButtons;
    }

    _scrollToPicker() {
      var form = this._form();
      var target = form && form.querySelector('button[type="submit"]'); // the true buy button, in its sticky slot
      if (!target) return;
      var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if ('scrollBehavior' in document.documentElement.style) {
        target.scrollIntoView({ block: 'end', behavior: reduced ? 'auto' : 'smooth' });
      } else {
        window.scrollBy(0, target.getBoundingClientRect().bottom - window.innerHeight); // browser floor: no options object
      }
      var first = this.querySelector('[data-bundle-inc]:not([disabled])');
      if (first) { try { first.focus({ preventScroll: true }); } catch (e) { /* noop */ } }
    }

    // "Add n packs" with the Slovak declension picked by count (SPEC §24):
    // atc_packs_1 (1), atc_packs_2_4 (2–4), atc_packs_5 (5+).
    _packsLabel(count) {
      var s = this._strings;
      var key = count === 1 ? s.atcPacks1 : count <= 4 ? s.atcPacks24 : s.atcPacks5;
      return interpolate(key, { count: count });
    }

    // Button label (SPEC §17.2.3, §18.4, §24, §25.4): no packs anywhere ->
    // the disabled "choose a flavour" label; additions -> "{Add n packs} ·
    // {total}" (n = additions); packs in the cart and nothing new ->
    // "{Add to cart} · {total}", which opens the cart without a request
    // (data-bundle-open-cart). Total = the full desired cart, full sum
    // struck when the total is below it. Original markup is cached so the
    // fail-safe can restore the theme's own button.
    _syncButtons() {
      var s = this._strings;
      if (!this._buttonCache) this._buttonCache = [];
      var cache = this._buttonCache;
      var total = this._total();
      var additions = this._additionsTotal();

      var buttons = this._submitButtons();
      var bar = this._themeBarButtons();
      for (var b = 0; b < bar.length; b++) {
        if (buttons.indexOf(bar[b]) === -1) buttons.push(bar[b]); // a bar button in picker mode is type="button"
      }
      var unavailable = this._hasUnavailable();
      for (var i = 0; i < buttons.length; i++) {
        var el = buttons[i];
        var entry = null;
        for (var j = 0; j < cache.length; j++) {
          if (cache[j].el === el) { entry = cache[j]; break; }
        }
        if (!entry) {
          // SPEC §42: labels go into the custom-button's content element; its
          // original markup is cached for the fail-safe.
          var target0 = this._buttonContent(el);
          entry = { el: el, target: target0, html: target0.innerHTML, disabled: el.disabled, type: el.type };
          cache.push(entry);
          this._observeBusy(el);
        }
        var target = entry.target && el.contains(entry.target) ? entry.target : this._buttonContent(el);

        var display = total > 0 ? this._priceDisplay() : null;
        var label = additions > 0 ? this._packsLabel(additions) : s.atcInCart;
        if (total > 0 && additions === 0) el.setAttribute('data-bundle-open-cart', '');
        else el.removeAttribute('data-bundle-open-cart');
        if (total === 0) {
          target.textContent = s.atcChooseFlavour;
        } else if (!display) {
          target.textContent = label;
        } else if (display.full) {
          target.innerHTML = interpolate(s.atcBundleDiscounted, { label: escapeHtml(label), total: escapeHtml(display.total), full: escapeHtml(display.full) });
        } else {
          target.textContent = interpolate(s.atcBundle, { label: label, total: display.total });
        }
        // SPEC §44: the theme bar's buttons scroll to the cards at 0 packs instead of sitting disabled
        var picker = bar.indexOf(el) !== -1 && total === 0 && !unavailable;
        if (picker) {
          if (el.type !== 'button') el.type = 'button';
          if (!el.hasAttribute('data-bundle-scroll-to-picker')) el.setAttribute('data-bundle-scroll-to-picker', '');
          el.disabled = false;
        } else {
          if (bar.indexOf(el) !== -1 && el.type !== 'submit') el.type = 'submit';
          if (el.hasAttribute('data-bundle-scroll-to-picker')) el.removeAttribute('data-bundle-scroll-to-picker');
          el.disabled = total === 0 || unavailable;
        }
      }
      this._syncLoadingClasses();
    }

    /* ----------------------------------------------------------- rendering */

    _renderAll() {
      this._renderLadder();
      this._renderHint();
      this._renderCards();
      this._renderPrice();
      this._syncButtons();
    }

    // Ladder (SPEC §24): Liquid rendered the markup; only the fill
    // (data-count, clamped to the 4 points) and the reached points change
    // with the total quantity. The per-pack text under points 2–4 is
    // written by _writeTier (SPEC §28.2); nothing else is rewritten.
    _renderLadder() {
      var ladder = this.querySelector('[data-bundle-ladder]');
      if (!ladder) return;
      var total = this._total();
      ladder.setAttribute('data-count', String(Math.min(total, FREE_EVERY)));
      var points = ladder.querySelectorAll('[data-bundle-point]');
      for (var i = 0; i < points.length; i++) {
        var n = parseInt(points[i].getAttribute('data-bundle-point'), 10);
        points[i].classList.toggle('is-reached', total >= n);
      }
    }

    // One hint line under the ladder (SPEC §24): count_0–4 are their own
    // keys; above 4, count_more with the packs left to the next free one
    // (every multiple of 4). Strong when one pack is missing to a free one.
    // The cheapest-free note shows when chosen prices differ and the
    // preview freed at least one pack (SPEC §5.5, §18.4).
    _renderHint() {
      var hint = this.querySelector('[data-bundle-hint]');
      var total = this._total();
      if (hint) {
        var copy = this._progressCopy(total);
        var cartPacks = this._cartPacks();
        // Packs in the cart and nothing new: "You have n packs in your cart." first (SPEC §25.4).
        if (cartPacks > 0 && this._additionsTotal() === 0) copy = this._inCartCopy(cartPacks) + ' ' + copy;
        hint.textContent = copy;
        hint.classList.toggle('is-strong', total > 0 && total % FREE_EVERY === FREE_EVERY - 1);
      }
      var cheapest = this.querySelector('[data-bundle-cheapest]');
      if (cheapest) cheapest.hidden = !(this._pricesDiffer() && this._freeUnits() > 0);
    }

    _progressCopy(count) {
      var s = this._strings;
      if (count <= 0) return s.progress0;
      if (count === 1) return s.progress1;
      if (count === 2) return s.progress2;
      if (count === 3) return s.progress3;
      if (count === 4) return s.progress4;
      return interpolate(s.progressMore, { count: FREE_EVERY - (count % FREE_EVERY) });
    }

    // Slovak declension by count (SPEC §25): in_cart_1 / in_cart_2_4 / in_cart_5.
    _inCartCopy(count) {
      var s = this._strings;
      var key = count === 1 ? s.inCart1 : count <= 4 ? s.inCart24 : s.inCart5;
      return interpolate(key, { count: count });
    }

    _renderCards() {
      var container = this.querySelector('[data-bundle-cards]');
      if (!container) return;

      var self = this;
      var groups = (this._data.groups || []).slice().sort(function (a, b) {
        if (a.key === self._currentGroupKey) return -1;
        if (b.key === self._currentGroupKey) return 1;
        return 0;
      });

      var html = '';
      groups.forEach(function (group) {
        var products = group.products || [];
        var allSoldOut = products.length === 0 || products.every(function (p) { return !p.available; });
        var added = 0;
        products.forEach(function (product) { added += self._qty(product.variantId); });

        html += '<div class="bundle-group' + (allSoldOut ? ' is-sold-out' : '') + '" data-group="' + escapeHtml(group.key) + '">';
        html += '<h3 class="bundle-group__heading"><span>' + escapeHtml(group.label) + '</span>' + self._countBadge(added) + '</h3>';
        html += '<div class="bundle-group__cards">';
        products.forEach(function (product) { html += self._cardHtml(product); });
        html += '</div></div>';
      });

      // Morph instead of replacing (SPEC §32): rebuilding every card with
      // innerHTML re-created the swatch <img>s on each quantity change, which
      // iOS Safari repaints as a blink. When the container already holds the
      // same groups and cards in the same order (the Liquid pre-render, or a
      // previous render), only what changed is swapped and the swatch node
      // is never touched; the resulting DOM is identical to the full render.
      var next = document.createElement('div');
      next.innerHTML = html;
      if (!this._morphCards(container, next)) container.innerHTML = html;
    }

    // Returns false when the structure differs (then the caller rebuilds).
    _morphCards(container, next) {
      var oldGroups = container.children, newGroups = next.children;
      if (oldGroups.length !== newGroups.length) return false;
      for (var g = 0; g < newGroups.length; g++) {
        var og = oldGroups[g], ng = newGroups[g];
        if (og.getAttribute('data-group') !== ng.getAttribute('data-group')) return false;
        var oldCards = og.querySelectorAll('.bundle-group__cards > .bundle-card');
        var newCards = ng.querySelectorAll('.bundle-group__cards > .bundle-card');
        if (oldCards.length !== newCards.length) return false;
        for (var c = 0; c < newCards.length; c++) {
          if (oldCards[c].getAttribute('data-bundle-card') !== newCards[c].getAttribute('data-bundle-card')) return false;
        }
      }
      for (g = 0; g < newGroups.length; g++) {
        og = oldGroups[g]; ng = newGroups[g];
        if (og.className !== ng.className) og.className = ng.className;
        var oh = og.querySelector('.bundle-group__heading'), nh = ng.querySelector('.bundle-group__heading');
        if (oh && nh && oh.innerHTML !== nh.innerHTML) oh.innerHTML = nh.innerHTML;
        oldCards = og.querySelectorAll('.bundle-group__cards > .bundle-card');
        newCards = ng.querySelectorAll('.bundle-group__cards > .bundle-card');
        for (c = 0; c < newCards.length; c++) this._morphCard(oldCards[c], newCards[c]);
      }
      return true;
    }

    // Copies the class list and every child except the swatch (kept in
    // place, its <img> untouched) from the freshly rendered card.
    _morphCard(oldCard, newCard) {
      if (oldCard.outerHTML === newCard.outerHTML) return;
      if (oldCard.className !== newCard.className) oldCard.className = newCard.className;
      var oldDot = null, i;
      var oldChildren = Array.prototype.slice.call(oldCard.children);
      for (i = 0; i < oldChildren.length; i++) {
        if (oldChildren[i].classList.contains('bundle-card__dot') && !oldDot) oldDot = oldChildren[i];
        else oldCard.removeChild(oldChildren[i]);
      }
      var newChildren = Array.prototype.slice.call(newCard.children);
      for (i = 0; i < newChildren.length; i++) {
        if (newChildren[i].classList.contains('bundle-card__dot')) {
          if (oldDot) { if (oldDot.outerHTML !== newChildren[i].outerHTML) { oldCard.replaceChild(newChildren[i], oldDot); } continue; }
        }
        oldCard.appendChild(newChildren[i]);
      }
    }

    // Card (SPEC §24, §28.3): swatch circle (the picker's product-variation__img /
    // __fill markup, or the swatch image in image mode, SPEC §22), flavour
    // name, the flavour_note line (no price since SPEC §31), the "in cart"
    // caption when the quantity is at or above the cart baseline (SPEC
    // §25.2), and the action: an "Add" button (bundle.builder.add) at 0
    // packs, a "− n +" stepper from 1 in a box of the same size, so nothing
    // moves (full-width pill layout, SPEC §31). Sold out: greyed, strikethrough, no action. After a 422
    // (is-unavailable) the "+" is disabled and a sold-out tag shows while
    // the "−" still works. At 0 packs this is byte for byte what
    // bundle-groups 'cards' renders (harness parity).
    _cardHtml(product) {
      var s = this._strings;
      var vid = product.variantId;
      var vidAttr = escapeHtml(String(vid));
      var qty = this._qty(vid);
      var cartQty = this._cartQty(vid);
      var inCart = qty > 0 && cartQty > 0 && qty >= cartQty; // at or above its cart baseline (SPEC §25.2)
      var unavailable = qty > 0 && this._unavailable[vid] === true;
      var soldOut = !product.available && !unavailable;
      var capped = !soldOut && !unavailable && !this._canAdd(product);

      var classes = 'bundle-card';
      if (soldOut) classes += ' is-sold-out';
      if (capped) classes += ' is-capped';
      if (qty > 0) classes += ' is-added';
      if (unavailable) classes += ' is-unavailable';

      // Same inline style as option-value.liquid's thumbnail: plain colour, or
      // the diagonal strike gradient when sold out.
      var colour = product.swatch || '#eee';
      var strike = !product.available;
      var fillStyle = strike
        ? 'background: linear-gradient(148deg, ' + colour + ' calc(50% - 1px), rgb(var(--text-color) / 0.6), ' + colour + ' calc(50% + 1px));'
        : 'background: ' + colour + ';';
      var dotClass = 'product-variation__img' + (strike ? ' vp-out-of-stock' : '');
      var dot = this._swatchStyle === 'image' && product.swatchImage
        ? '<span class="bundle-card__dot bundle-card__dot--image ' + dotClass + '"><img src="' + escapeHtml(product.swatchImage) + '" alt="" loading="lazy" decoding="async" width="240" height="240"></span>'
        : '<span class="bundle-card__dot ' + dotClass + '"><span class="product-variation__fill" style="' + escapeHtml(fillStyle) + '"></span></span>';

      var body = '<span class="bundle-card__name product-variation__title">' + escapeHtml(product.flavour) + '</span>';
      if (product.flavourNote) {
        body += '<span class="bundle-card__note">' + escapeHtml(product.flavourNote) + '</span>';
      }
      if (inCart) {
        body += '<span class="bundle-card__caption">' + escapeHtml(s.inCartCaption) + '</span>';
      }

      var incAttrs = ' data-bundle-inc="' + vidAttr + '" aria-label="' + escapeHtml(interpolate(s.increase, { flavour: product.flavour })) + '"';
      if (unavailable || soldOut) incAttrs += ' disabled title="' + escapeHtml(s.soldOut) + '"'; // soldOut only matters from 1 pack (in the cart)
      else if (capped) incAttrs += ' disabled title="' + escapeHtml(interpolate(s.onlyNLeft, { count: this._cappedCount(product) })) + '"';

      var action;
      if (qty === 0) {
        action = soldOut
          ? '<span class="sr-only">' + escapeHtml(s.soldOut) + '</span>'
          : '<button type="button" class="bundle-card__add"' + incAttrs + '>' + escapeHtml(s.add) + '</button>';
      } else {
        action = '<span class="bundle-card__stepper">' +
          '<button type="button" class="bundle-card__dec" data-bundle-dec="' + vidAttr + '" aria-label="' + escapeHtml(interpolate(s.decrease, { flavour: product.flavour })) + '">−</button>' +
          '<span class="bundle-card__qty">' + qty + '</span>' +
          '<button type="button" class="bundle-card__inc"' + incAttrs + '>+</button>' +
          '</span>';
        if (unavailable) action += '<span class="bundle-card__tag">' + escapeHtml(s.soldOut) + '</span>';
      }

      return '<div class="' + classes + '" data-bundle-card="' + vidAttr + '">' +
        dot +
        '<span class="bundle-card__body">' + body + '</span>' +
        action +
        '</div>';
    }

    _countBadge(count) {
      if (count === 0) return '';
      return '<span class="bundle-builder__count-badge">' + escapeHtml(interpolate(this._strings.addedCount, { count: count })) + '</span>';
    }
  }

  window.customElements.define('liva-bundle-builder', LivaBundleBuilder);
})();
